/**
 * API service for HTTP requests to the backend
 */

import axios from 'axios';
import { Conversation } from '../stores/conversationStore';
import { Message } from '../stores/messageStore';
import { afterUnauthorized, leaveForLogin } from '../components/Auth/authProject';
import { getImpersonationUserId } from '../stores/impersonationStore';
import { coalesceRequest } from './requestCoalescer';
import { authorizeRequest } from './authHeaders';

// Configure axios base URL
// Priority: 1. Runtime config (window.__ENV__), 2. Build-time env var, 3. Fallback to /api
export const getApiBaseUrl = () => {
  // @ts-ignore - window.__ENV__ is injected at runtime by Docker entrypoint
  const runtimeApiUrl = (window as any).__ENV__?.API_URL;
  const buildTimeApiUrl = import.meta.env.VITE_API_URL;

  if (runtimeApiUrl) {
    console.log('Using runtime API URL:', runtimeApiUrl);
    return `${runtimeApiUrl}/api`;
  }
  if (buildTimeApiUrl && buildTimeApiUrl !== '/api') {
    console.log('Using build-time API URL:', buildTimeApiUrl);
    return `${buildTimeApiUrl}/api`;
  }
  console.log('Using fallback API URL: /api');
  return '/api'; // Fallback for local development with proxy
};

/**
 * Get the API server base URL (without /api suffix)
 * Used for building absolute URLs for iframe fetch calls
 *
 * Returns: 'https://agentic-ivy-api-server-....run.app' or ''
 */
export const getApiServerUrl = (): string => {
  // @ts-ignore - window.__ENV__ is injected at runtime by Docker entrypoint
  const runtimeApiUrl = (window as any).__ENV__?.API_URL;
  const buildTimeApiUrl = import.meta.env.VITE_API_URL;

  if (runtimeApiUrl) {
    return runtimeApiUrl;
  }
  if (buildTimeApiUrl && buildTimeApiUrl !== '/api') {
    return buildTimeApiUrl;
  }
  // Empty string for local dev - relative URLs work when same origin
  return '';
};

const api = axios.create({
  // Don't set baseURL here - it will be set dynamically in the interceptor
  timeout: 600000, // 10 minutes - for long-running operations like video generation (scene timing: 2-5min, assembly: ~2min)
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(authorizeRequest, (error) => Promise.reject(error));

// Public routes that don't require authentication
// 401s on these routes should NOT trigger redirect to login
const PUBLIC_ROUTES = ['/video/', '/embed/', '/share/', '/login', '/waitlist', '/signup-flow', '/agent-chat', '/developers/quickstart', '/developers/kubicle'];

const isOnPublicRoute = (): boolean => {
  const currentPath = window.location.pathname;
  return PUBLIC_ROUTES.some(route => currentPath.startsWith(route));
};

// Response interceptor for handling errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      if (isOnPublicRoute()) {
        return Promise.reject(new Error('UNAUTHENTICATED'));
      }

      // All concurrent 401s share one handover or one refresh; each request retries at most once
      const verdict = await afterUnauthorized(error.response.data, error.config._authRetried === true);
      if (verdict === 'retry') {
        error.config._authRetried = true;
        return api.request(error.config);
      }

      console.error('[AUTH] Unrecoverable 401. URL:', error.config?.url);
      await leaveForLogin(verdict);
      return Promise.reject(new Error('UNAUTHENTICATED'));
    }

    if (error.response?.status === 403 && error.response?.data?.error === 'WORKER_MISMATCH') {
      // Worker assignment mismatch - redirect to login
      console.error('Worker assignment mismatch - unauthorized access detected');
      console.error('Assigned to:', error.response.data.assignedTo);
      // Clear any stored tokens
      localStorage.removeItem('bearer_token');
      // Redirect to login page
      window.location.href = '/login';
      // Prevent further processing
      return Promise.reject(new Error('WORKER_MISMATCH'));
    }

    if (error.response?.status === 403 && error.response?.data?.code === 'APPROVAL_REQUIRED') {
      // User account not approved - redirect to waitlist
      console.warn('Account pending approval - redirecting to waitlist');
      // Redirect to waitlist page (don't clear token - they're authenticated)
      if (!window.location.pathname.startsWith('/waitlist')) {
        window.location.href = '/waitlist';
      }
      return Promise.reject(new Error('APPROVAL_REQUIRED'));
    }

    // Billing paywall — trigger global upgrade modal (dynamic import to avoid
    // circular store→api cycles at module load).
    if (
      error.response?.status === 402 ||
      (error.response?.data?.code && ['NO_PLAN', 'OUT_OF_CREDITS', 'EXPORT_NOT_ALLOWED', 'LENGTH_EXCEEDED'].includes(error.response.data.code))
    ) {
      import('../stores/upgradeModalStore')
        .then(({ useUpgradeModal }) => {
          const raw = error.response?.data;
          useUpgradeModal.getState().open({
            code: raw?.code,
            message: typeof raw?.error === 'string' ? raw.error : raw?.error?.message,
          });
        })
        .catch(() => void 0);
    }

    if (error.response?.status >= 500) {
      console.error('Server error:', error.response.data);
    }

    return Promise.reject(error);
  }
);

// Health check
export const healthCheck = async () => {
  const response = await api.get('/health');
  return response.data;
};

// Get available models
export const getModels = async () => {
  const response = await api.get('/models');
  return response.data;
};

// Conversation API
// Custom timeout for message-heavy operations
const messageAPI = axios.create({
  // Don't set baseURL here - it will be set dynamically in the interceptor
  timeout: 60000, // 60 seconds for message operations
  headers: {
    'Content-Type': 'application/json',
  },
});

messageAPI.interceptors.request.use(authorizeRequest, (error) => Promise.reject(error));

interface MeTeam {
  id: string;
  name: string;
  organizationId: string;
  organization: { id: string; name: string; slug: string } | null;
  role: string;
}

export interface MeResponse {
  user: {
    id: string;
    email: string;
    displayName: string | null;
    photoUrl: string | null;
    role: string;
    /** Clickwrap consent state. */
    termsAcceptedAt?: string | null;
    termsVersion?: string | null;
    /** True when the user hasn't accepted the current Terms version. */
    needsTermsAcceptance?: boolean;
    /** Account creation time (ISO), when the API provides it — the sign_up signal. */
    createdAt?: string;
  };
  teams: MeTeam[];
  activeTeam: MeTeam | null;
  activeOrganization: { id: string; name: string; slug: string } | null;
  /** DB-backed feature flags resolved for this (possibly impersonated) user. */
  features: string[];
  /** The Terms version currently in effect. */
  currentTermsVersion?: string;
}

export const meAPI = {
  get: async (): Promise<MeResponse> => {
    const response = await api.get('/me');
    return response.data;
  },
  acceptTerms: async (): Promise<{ ok: boolean; termsVersion: string; termsAcceptedAt: string }> => {
    const response = await api.post('/me/accept-terms');
    return response.data;
  },
  setActiveTeam: async (teamId: string): Promise<{ ok: boolean; activeTeamId: string; activeOrganizationId: string }> => {
    const response = await api.put('/me/active-team', { teamId });
    return response.data;
  },
};

// D1 (17 Oct 2026): the pack cards and checkout are the one place a price is shown; elsewhere, credits only (F8).
export interface CreditPack {
  id: string;
  credits: number;
  label: string;
  popular?: boolean;
  /** About how many videos the pack makes; null for a pack without an estimate. */
  videosApprox: number | null;
  /** List price in USD. */
  listUsd: number;
  /** What checkout charges: the list price less the early-user discount. */
  priceUsd: number;
  discountPct: number;
}

export interface CreditPacksResponse {
  packs: CreditPack[];
  pricingVersion: string;
  /** The early-user discount every pack is priced at, 0 when off. */
  earlyDiscountPct: number;
  /** Credits a new account starts with. */
  freeCredits: number;
}

export type CreditActivityKind = 'usage' | 'fee' | 'grant' | 'purchase' | 'clawback' | 'migration' | 'adjustment';

export interface CreditBalance {
  scope: 'user' | 'org';
  organizationId?: string;
  credits: number;
  available: number;
}

export interface CreditActivityVideoRef {
  id: string;
  title: string;
}

export interface CreditActivityEntry {
  id: string;
  at: string;
  kind: CreditActivityKind;
  label: string;
  credits: number;
  balanceAfter: number | null;
  video: CreditActivityVideoRef | null;
}

export interface CreditActivityPage {
  balance: CreditBalance;
  entries: CreditActivityEntry[];
  nextCursor: string | null;
}

export interface CreditActivityVideoGroup {
  video: CreditActivityVideoRef;
  /** The credits the video was charged in total (usage and its fee), never split. */
  charged: number;
  qualityTier: string | null;
  lastAt: string | null;
}

export interface CreditActivityByVideo {
  balance: CreditBalance;
  videos: CreditActivityVideoGroup[];
}

export interface CreditActivityParams {
  limit?: number;
  cursor?: string;
  from?: string;
  to?: string;
  kind?: CreditActivityKind;
}

export interface WalletBalance {
  balanceMilli: number;
  availableMilli: number;
  credits: string;
  lowWater: boolean;
  lowWaterMilli: number;
  scope: 'user' | 'org';
}

export const billingAPI = {
  getBalance: async (): Promise<WalletBalance> => (await api.get('/billing/balance')).data,
  getActivity: async (params: CreditActivityParams = {}): Promise<CreditActivityPage> =>
    (await api.get('/billing/activity', { params })).data,
  getActivityByVideo: async (params: { from?: string; to?: string; videoId?: string } = {}): Promise<CreditActivityByVideo> =>
    (await api.get('/billing/activity/videos', { params })).data,
  getEntitlement: async () => (await api.get('/billing/entitlement')).data,
  getPacks: async (): Promise<CreditPacksResponse> =>
    (await api.get('/billing/packs')).data,
  // Returns the Stripe Checkout URL to redirect to. Pass a credit-pack id — packs
  // are the only self-serve purchase (no subscription plans).
  startCheckout: async (opts: { pack: string }): Promise<{ url: string; sessionId: string }> => {
    const params = new URLSearchParams();
    params.set('pack', opts.pack);
    return (await api.get(`/billing/checkout?${params.toString()}`)).data;
  },
  openPortal: async (): Promise<{ url: string }> => (await api.post('/billing/portal')).data,
  setAutoTopUp: async (packId: string | null): Promise<{ autoTopUpPackId: string | null }> =>
    (await api.put('/billing/auto-topup', { packId })).data,
};

export const conversationAPI = {
  // Get all conversations (with optional pagination)
  getAll: async (options?: { limit?: number; offset?: number; collectionTypes?: string[] }): Promise<{
    conversations: Conversation[];
    pagination?: { total: number; limit: number; offset: number; hasMore: boolean };
  }> => {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    if (options?.collectionTypes?.length) params.set('collectionTypes', options.collectionTypes.join(','));
    const qs = params.toString();
    const response = await api.get(`/conversations${qs ? `?${qs}` : ''}`);
    return response.data;
  },

  // Get conversations by team (with optional pagination)
  getByTeam: async (teamId: string, options?: { limit?: number; offset?: number; collectionTypes?: string[] }): Promise<{
    conversations: Conversation[];
    pagination?: { total: number; limit: number; offset: number; hasMore: boolean };
  }> => {
    const params = new URLSearchParams({ teamId });
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    if (options?.collectionTypes?.length) params.set('collectionTypes', options.collectionTypes.join(','));
    const response = await api.get(`/conversations?${params.toString()}`);
    return response.data;
  },

  // Get conversation by ID
  getById: async (id: string): Promise<{ conversation: Conversation }> => {
    const response = await api.get(`/conversations/${id}`);
    return response.data;
  },

  // Create new conversation with optional project initialization
  create: async (data: {
    title?: string;
    model?: string;
    initializeProject?: boolean;
    workingDirectory?: string;
    styleId?: string;
    organizationId: string;
    teamId: string;
    collectionType?: 'VIDEO' | 'STYLE' | 'SLIDE';
    projectKind?: 'assessment';
    designSystemId?: string;
  }): Promise<{ conversation: Conversation }> => {
    const response = await api.post('/conversations', data);
    return response.data;
  },

  // Update conversation
  update: async (id: string, data: { title?: string; model?: string; styleId?: string }): Promise<{ conversation: Conversation }> => {
    const response = await api.put(`/conversations/${id}`, data);
    return response.data;
  },

  // Delete conversation
  delete: async (id: string): Promise<{ success: boolean; deletedMessages: number; errors: string[] }> => {
    const response = await api.delete(`/conversations/${id}`);
    return response.data;
  },

  // Clone a video project (outline + slides + audio + storage files)
  clone: async (id: string, options?: { newTitle?: string }): Promise<{
    success: boolean;
    newProjectId: string;
    slidesCopied: number;
    audioCopied: number;
    filesCopied: number;
    errors: string[];
  }> => {
    const response = await api.post(`/conversations/${id}/clone`, options ?? {});
    return response.data;
  },

  // Delete multiple conversations
  deleteMultiple: async (conversationIds: string[]): Promise<{
    success: boolean;
    totalDeleted: number;
    totalMessages: number;
    failed: string[];
    errors: string[]
  }> => {
    const response = await api.delete('/conversations', { data: { conversationIds } });
    return response.data;
  },

  // Reset conversation checkpoint to start fresh context
  resetCheckpoint: async (id: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/conversations/${id}/reset-checkpoint`);
    return response.data;
  },

  // Get messages for conversation
  getMessages: async (
    id: string,
    options?: { limit?: number; offset?: number }
  ): Promise<{
    messages: Message[];
    pagination?: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    };
    hasMore?: boolean;
  }> => {
    const params = new URLSearchParams();
    if (options?.limit !== undefined) params.append('limit', options.limit.toString());
    if (options?.offset !== undefined) params.append('offset', options.offset.toString());

    // Use messageAPI with longer timeout for message operations
    const response = await messageAPI.get(`/conversations/${id}/messages?${params.toString()}`);
    return response.data;
  },

  // Get conversation version for cache busting
  getVersion: async (id: string): Promise<{ success: boolean; version: string }> => {
    const response = await api.get(`/conversations/${id}/version`);
    return response.data;
  },

  // Stream restoration APIs
  getStreamStatus: async (id: string): Promise<{
    success: boolean;
    data: {
      conversationId: string;
      isStreaming: boolean;
      messageId: string | null;
      lastChunkIndex: number;
      startedAt: Date | null;
      lastActivity: Date | null;
      streamUrl: string | null;
      hasActiveSession: boolean;
    }
  }> => {
    const response = await api.get(`/conversations/${id}/stream-status`);
    return response.data;
  },

  getChunksSince: async (conversationId: string, messageId: string, sinceIndex: number): Promise<{
    success: boolean;
    data: {
      conversationId: string;
      messageId: string;
      sinceIndex: number;
      chunks: Array<{
        index: number;
        content: string;
        type: 'content' | 'thinking' | 'tool_call' | 'tool_response' | 'complete';
        timestamp: Date;
        metadata?: Record<string, any>;
      }>;
      totalChunks: number;
      hasMore: boolean;
    }
  }> => {
    const response = await api.get(`/conversations/${conversationId}/messages/${messageId}/chunks/since/${sinceIndex}`);
    return response.data;
  },

  getAllChunks: async (conversationId: string, messageId: string): Promise<{
    success: boolean;
    data: {
      conversationId: string;
      messageId: string;
      chunks: Array<{
        index: number;
        content: string;
        type: 'content' | 'thinking' | 'tool_call' | 'tool_response' | 'complete';
        timestamp: Date;
        metadata?: Record<string, any>;
      }>;
      reconstructedContent: string;
      thinkingEvents: Array<{
        content: string;
        subject?: string;
        timestamp: Date;
      }>;
      totalChunks: number;
      contentLength: number;
    }
  }> => {
    const response = await api.get(`/conversations/${conversationId}/messages/${messageId}/chunks`);
    return response.data;
  },

  /**
   * Get active message jobs for a conversation
   * Used to show "AI is processing" indicator on page load/refresh
   * Returns PENDING and PROCESSING jobs with their message info
   *
   * @param contextSource - Optional filter by context source ('outline' | 'narration' | 'studio')
   */
  getActiveMessageJobs: async (
    conversationId: string,
    options?: { contextSource?: 'outline' | 'narration' | 'studio' }
  ): Promise<{
    success: boolean;
    jobs: Array<{
      jobId: string;
      messageId: string;
      conversationId: string;
      status: 'pending' | 'active';
      progress: any;
      createdAt: string;
      startedAt: string | null;
      message: {
        id: string;
        content: string;
        isStreaming: boolean;
        streamCompleted: boolean;
        createdAt: string;
      } | null;
    }>;
    hasActiveJob: boolean;
    activeMessageId: string | null;
  }> => {
    const params = options?.contextSource ? `?contextSource=${options.contextSource}` : '';
    const response = await api.get(`/conversations/${conversationId}/message-jobs/active${params}`);
    return response.data;
  },

  /**
   * Get conversation lock status
   * Check if a job is currently processing for this conversation
   * Used to prevent duplicate message sends after page refresh
   */
  getLockStatus: async (conversationId: string): Promise<{
    conversationId: string;
    locked: boolean;
    jobId: string | null;
    messageId: string | null;
    acquiredAt: number | null;
    lockedDuration: number | null;
  }> => {
    const response = await api.get(`/conversations/${conversationId}/lock-status`);
    return response.data;
  },
};

// File upload API
export const fileAPI = {
  // Upload files
  upload: async (files: FileList): Promise<{ files: any[] }> => {
    const formData = new FormData();
    Array.from(files).forEach((file) => {
      formData.append('files', file);
    });

    const response = await api.post('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Delete file
  delete: async (fileId: string): Promise<{ success: boolean }> => {
    const response = await api.delete(`/files/${fileId}`);
    return response.data;
  },
};

// Resources API
export const resourcesAPI = {
  // Get resources for a conversation
  get: async (conversationId: string): Promise<{
    success: boolean;
    resources: {
      images: Array<{
        name: string;
        path: string;
        size: number;
        uploadedAt: string;
        category: string;
      }>;
      documents: Array<{
        name: string;
        path: string;
        size: number;
        uploadedAt: string;
        category: string;
      }>;
      others: Array<{
        name: string;
        path: string;
        size: number;
        uploadedAt: string;
        category: string;
      }>;
    };
  }> => {
    const response = await api.get(`/conversations/${conversationId}/resources`);
    return response.data;
  },

  // Delete resources from a conversation
  delete: async (conversationId: string, filePaths: string[]): Promise<{
    success: boolean;
    message: string;
    results: Array<{
      path: string;
      success: boolean;
      error?: string;
    }>;
    summary: {
      total: number;
      successful: number;
      failed: number;
    };
    gitCommit: string | null;
  }> => {
    const response = await api.delete(`/conversations/${conversationId}/resources`, {
      data: { files: filePaths }
    });
    return response.data;
  },

  // Upload a media asset (image/video) to a conversation
  uploadMedia: async (
    conversationId: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<{
    success: boolean;
    originalName: string;
    savedName: string;
    size: number;
    resourceType: string;
    mimeType: string;
    resourceId: string;
    gcsPath: string;
    signedUrl: string;
    baseUrl?: string;
  }> => {
    const formData = new FormData();
    formData.append('conversationId', conversationId);
    formData.append('files', file);
    formData.append('purposes', JSON.stringify(['ADD_TO_SCENES']));

    const response = await api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000, // 5 minutes
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          onProgress(progress);
        }
      },
    });

    if (response.data.success && response.data.results?.[0]?.success) {
      return {
        success: true,
        ...response.data.results[0],
      };
    }
    throw new Error(response.data.results?.[0]?.error || 'Upload failed');
  },
};

// Filesystem API
export const filesystemAPI = {
  // Browse directory contents
  browse: async (path: string, conversationId?: string): Promise<{ files: any[] }> => {
    // Handle root directory case
    const cleanPath = path === '.' ? '' : path;
    const encodedPath = cleanPath ? encodeURIComponent(cleanPath) : '';
    const url = encodedPath ? `/filesystem/browse/${encodedPath}` : '/filesystem/browse/';
    const params = conversationId ? `?conversationId=${conversationId}` : '';
    const response = await api.get(url + params);
    return response.data;
  },

  // Read file contents
  read: async (path: string, conversationId?: string): Promise<{ content: string; extension: string; size: number }> => {
    const encodedPath = encodeURIComponent(path);
    const params = conversationId ? `?conversationId=${conversationId}` : '';
    const response = await api.get(`/filesystem/read/${encodedPath}${params}`);
    return response.data;
  },

  // Get file/directory info
  info: async (path: string, conversationId?: string): Promise<{ path: string; type: 'file' | 'directory'; name: string; extension?: string; size?: number; lastModified?: string }> => {
    const encodedPath = encodeURIComponent(path);
    const params = conversationId ? `?conversationId=${conversationId}` : '';
    const response = await api.get(`/filesystem/info/${encodedPath}${params}`);
    return response.data;
  },

  // Write/update file contents
  write: async (path: string, content: any, conversationId: string, createBackup: boolean = true): Promise<{ success: boolean; path: string; backupCreated: boolean }> => {
    const encodedPath = encodeURIComponent(path);
    const response = await api.put(`/conversations/${conversationId}/files/${encodedPath}`, {
      content,
      createBackup
    });
    return response.data;
  },

  // Update asset history
  updateAssetHistory: async (conversationId: string, data: {
    slidePath: string;
    assetId: string;
    originalUrl: string;
    newUrl: string;
    source: string;
    attribution?: string;
    layerIndex?: number;
    propertyType?: string;
    elementPath?: string;
  }): Promise<{ success: boolean; historyEntry: any }> => {
    const response = await api.post(`/conversations/${conversationId}/asset-history`, data);
    return response.data;
  },

  // Get asset history
  getAssetHistory: async (slidePath: string, conversationId: string, assetId?: string): Promise<any> => {
    const encodedPath = encodeURIComponent(slidePath);
    // Build path without /api prefix since axios instance already has baseURL: '/api'
    let path = `/conversations/${conversationId}/asset-history/${encodedPath}?conversationId=${conversationId}`;
    if (assetId) {
      path += `&assetId=${encodeURIComponent(assetId)}`;
    }
    const response = await api.get(path);
    return response.data;
  },

  // Get project-wide asset library
  getProjectAssetLibrary: async (conversationId: string): Promise<{ library: Record<string, any>; totalAssets: number }> => {
    const response = await api.get(`/conversations/${conversationId}/asset-library`);
    return response.data;
  },

  // Initialize slide assets - batch capture all current assets as baseline
  initializeSlideAssets: async (conversationId: string, data: {
    slidePath: string;
    assets: Array<{
      assetId: string;
      url: string;
      layerIndex: number;
      propertyType: string;
      elementPath: string;
    }>;
  }): Promise<{ success: boolean; assetsInitialized: number; assetsSkipped: number; totalAssets: number }> => {
    const response = await api.post(`/conversations/${conversationId}/assets/initialize`, data);
    return response.data;
  },
};

// Conversation directory API
export const conversationDirectoryAPI = {
  // Get conversation working directory
  get: async (conversationId: string): Promise<{ workingDirectory: string; isActive: boolean }> => {
    const response = await api.get(`/conversations/${conversationId}/directory`);
    return response.data;
  },

  // Update conversation working directory
  update: async (conversationId: string, workingDirectory: string): Promise<{ success: boolean; workingDirectory: string }> => {
    const response = await api.put(`/conversations/${conversationId}/directory`, {
      workingDirectory
    });
    return response.data;
  },
};

// Slides API
export interface AssessmentStudioQuestion {
  id: string;
  format: string;
  concept?: string;
  conceptTags?: string[];
  archetype?: string;
  difficulty?: string;
  stem?: string;
  explanation?: string;
  options?: string[];
  answer?: any;
  correct?: any;
  experienceFile?: string;
  verify?: { correct?: any; wrong?: any };
  html?: string | null;
  missing?: boolean;
  [k: string]: any;
}

export interface AssessmentStudioData {
  conversationId: string;
  assessmentId: string | null;
  title: string | null;
  meta: { subject?: string | null; grade?: string | null; conceptNamespace?: string | null; passThreshold?: number; ncert?: any };
  designSystemId?: string | null;
  questions: AssessmentStudioQuestion[];
}

export interface AssessmentShareLinkInfo {
  id: string; shareToken: string; shareUrl: string; embedUrl: string;
  isActive: boolean; allowReviews: boolean; viewCount: number; lastViewedAt: string | null;
  createdAt: string; updatedAt: string | null; title: string | null;
  questionCount: number; isOutdated: boolean;
}
export interface AssessmentAnalytics {
  published: boolean; shareToken: string | null; shareUrl?: string;
  totals?: { statements: number; graded: number; correct: number; learners: number };
  timing?: { completions: number; avgActiveMs: number | null; avgWallMs: number | null; avgIdleMs: number | null };
  concepts?: { tag: string; total: number; correct: number; pct: number }[];
  perQuestion?: { id: string; type: string; total: number; correct: number; pct: number; timing?: { avgActiveMs: number | null; avgTimeToAnswerMs: number | null; avgVisits: number | null } | null }[];
}

export const assessmentAPI = {
  // Read an unpublished assessment project's plan + per-question meta + experience HTML
  // (from the synced production state). Edits flow through ivy-chat → MCP, not here.
  get: async (conversationId: string): Promise<AssessmentStudioData> =>
    (await api.get(`/conversations/${conversationId}/assessment`)).data,
  publishStatus: async (conversationId: string): Promise<{ isPublished: boolean; shareLinks: AssessmentShareLinkInfo[] }> =>
    (await api.get(`/conversations/${conversationId}/assessment/publish-status`)).data,
  publish: async (conversationId: string): Promise<{ success: boolean; shareLink?: AssessmentShareLinkInfo; error?: string; issues?: string[] }> =>
    (await api.post(`/conversations/${conversationId}/assessment/publish`)).data,
  updateShareLink: async (conversationId: string, shareLinkId: string): Promise<{ success: boolean; shareLink?: AssessmentShareLinkInfo; error?: string }> =>
    (await api.put(`/conversations/${conversationId}/assessment/publish/${shareLinkId}`)).data,
  revokeShareLink: async (conversationId: string, shareLinkId: string): Promise<{ success: boolean; message?: string }> =>
    (await api.delete(`/conversations/${conversationId}/assessment/publish/${shareLinkId}`)).data,
  analytics: async (conversationId: string): Promise<AssessmentAnalytics> =>
    (await api.get(`/conversations/${conversationId}/assessment/analytics`)).data,
  listQuestionVersions: async (conversationId: string, qId: string): Promise<{ enabled: boolean; currentVersion: number | null; versions: any[] }> =>
    (await api.get(`/conversations/${conversationId}/assessment/questions/${qId}/versions`)).data,
  activateQuestionVersion: async (conversationId: string, qId: string, version: number): Promise<{ success: boolean; version?: number }> =>
    (await api.post(`/conversations/${conversationId}/assessment/questions/${qId}/versions/${version}/activate`)).data,
};

export interface LabAsset {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  slideId: string | null;
  currentVersion: number;
  updatedAt: string;
  viewUrl: string;
}

export interface LabAssetVersion {
  version: number;
  changeSummary: string | null;
  createdBy: string;
  createdAt: string;
  viewUrl: string;
}

export const labAssetsAPI = {
  // Agent-authored HTML prototypes shown in the "Lab" tab (read-only — publishing is via the sync_lab_asset_to_production MCP tool)
  get: async (conversationId: string): Promise<{ assets: LabAsset[] }> =>
    (await api.get(`/conversations/${conversationId}/lab-assets`)).data,
  getVersions: async (conversationId: string, slug: string): Promise<{ versions: LabAssetVersion[] }> =>
    (await api.get(`/conversations/${conversationId}/lab-assets/${slug}/versions`)).data,
};

export const slidesAPI = {
  // Get slides for a specific conversation (V2 API - uses database as source of truth)
  getByConversation: async (conversationId: string, bustCache: boolean = false): Promise<{ slides: any[] }> => {
    // Use V2 API endpoint that queries the Slide table (works with GCS-backed slides)
    const url = bustCache
      ? `/conversations/${conversationId}/slides/list?t=${Date.now()}`
      : `/conversations/${conversationId}/slides/list`;

    const response = await api.get(url, {
      // Disable caching headers
      headers: bustCache ? {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      } : {}
    });
    return response.data;
  },

  // Fetch GSAP slide JSON data
  // Supports both slideId (e.g., "slide-001") for GCS-backed slides
  // and legacy paths (e.g., "slide/slide.json") for filesystem slides
  getGsapSlide: async (slideIdOrPath: string, conversationId?: string, servePin?: string | null): Promise<any> => {
    // Detect if this is a slideId (format: "slide-001" or "slide-NNN")
    const isSlideId = /^slide-\d+$/.test(slideIdOrPath);

    if (isSlideId && conversationId) {
      // Use new GCS-backed endpoint via SlideVersion table. servePin is the
      // manifest-harvested "v=..&aa=.." query: a matching ?v= makes /serve
      // reply immutable-cacheable, and aa rotates the cache key on every
      // audio apply; on mismatch the server replies no-cache with current
      // content, so a stale pin degrades to the unpinned behavior.
      const url = `/conversations/${conversationId}/slides/${slideIdOrPath}/serve${servePin ? `?${servePin}` : ''}`;
      const response = await api.get(url);
      return response.data;
    } else {
      // Legacy filesystem path
      const url = `/filesystem/read/${slideIdOrPath}${conversationId ? `?conversationId=${conversationId}` : ''}`;
      const response = await api.get(url);

      // Parse JSON content if it's a string
      if (typeof response.data.content === 'string') {
        return JSON.parse(response.data.content);
      }

      return response.data.content;
    }
  },

  // Check if a slide path is GSAP format
  isGsapSlide: (path: string): boolean => {
    return path.endsWith('.json');
  },

  // Get JSON serving URL for GSAP slides
  // For GCS-backed slides, use slideId. For legacy, use path.
  // Includes impersonation user ID for admin "View As" functionality in iframes
  getJSONUrl: (conversationId: string, slideIdOrPath: string, version?: number): string => {
    const baseUrl = getApiBaseUrl();
    const token = localStorage.getItem('bearer_token');
    const impersonateUserId = getImpersonationUserId();

    // Detect if this is a slideId (format: "slide-001") or a path (contains slashes)
    const isSlideId = /^slide-\d+$/.test(slideIdOrPath) || !slideIdOrPath.includes('/');

    if (isSlideId) {
      // GCS-backed slide serving via SlideVersion table
      const params = new URLSearchParams();
      if (version !== undefined) {
        params.set('version', version.toString());
      }
      if (token) {
        params.set('token', token);
      }
      if (impersonateUserId) {
        params.set('impersonate', impersonateUserId);
      }
      const queryString = params.toString();
      return `${baseUrl}/conversations/${conversationId}/slides/${slideIdOrPath}/serve${queryString ? '?' + queryString : ''}`;
    } else {
      // Legacy filesystem path - fallback to old endpoint
      const encodedPath = encodeURIComponent(slideIdOrPath);
      const params = new URLSearchParams();
      params.set('conversationId', conversationId);
      if (token) {
        params.set('token', token);
      }
      if (impersonateUserId) {
        params.set('impersonate', impersonateUserId);
      }
      return `${baseUrl}/filesystem/json/${encodedPath}?${params.toString()}`;
    }
  },

  // Save slide edits
  saveSlideEdits: async (payload: {
    conversationId: string;
    slideId: string;
    slidePath: string;
    editData: any;
    timestamp: string;
  }): Promise<{
    success: boolean;
    message: string;
    path: string;
    timestamp: string;
    format?: 'source' | 'legacy';
    versioning?: {
      enabled: boolean;
      currentVersion: number | string | null;
      storageMode: 'gcs' | 'filesystem';
    };
  }> => {
    const response = await api.post(`/conversations/${payload.conversationId}/slides/save`, {
      slideId: payload.slideId,
      slidePath: payload.slidePath,
      editData: payload.editData,
      timestamp: payload.timestamp
    });
    return response.data;
  },

  // Recompile slide from source files
  recompileSlide: async (payload: {
    conversationId: string;
    slideId: string;
    slidePath: string;
  }): Promise<{ success: boolean; message: string; slideDir: string; timestamp: string }> => {
    const response = await api.post(
      `/conversations/${payload.conversationId}/slides/${payload.slideId}/recompile`,
      { slidePath: payload.slidePath }
    );
    return response.data;
  },

  // Version management methods

  // List all versions for a slide
  listVersions: async (conversationId: string, slideId: string): Promise<{
    enabled: boolean;
    currentVersion: number | null;
    versions: Array<{
      version: number;
      timestamp: string;
      message: string;
      author: string;
      changes: {
        type: 'create' | 'edit' | 'restore';
        summary: string;
        elements?: string[];
      };
    }>;
    maxVersions: number;
  }> => {
    const response = await api.get(`/conversations/${conversationId}/slides/${slideId}/versions`);
    return response.data;
  },

  // Get specific version info
  getVersionInfo: async (conversationId: string, slideId: string, version: number): Promise<{
    version: number;
    timestamp: string;
    message: string;
    author: string;
    changes: {
      type: 'create' | 'edit' | 'restore';
      summary: string;
      elements?: string[];
    };
  }> => {
    const response = await api.get(`/conversations/${conversationId}/slides/${slideId}/versions/${version}`);
    return response.data;
  },

  // Switch to a specific version
  switchToVersion: async (conversationId: string, slideId: string, version: number): Promise<{
    success: boolean;
    message: string;
    currentVersion: number;
  }> => {
    const response = await api.post(`/conversations/${conversationId}/slides/${slideId}/versions/${version}/activate`);
    return response.data;
  },

  // Compare two versions
  compareVersions: async (conversationId: string, slideId: string, v1: number, v2: number): Promise<{
    version1: number;
    version2: number;
    changes: Array<{
      file: string;
      type: 'added' | 'removed' | 'modified';
      diff?: string;
    }>;
  }> => {
    const response = await api.get(`/conversations/${conversationId}/slides/${slideId}/versions/compare?v1=${v1}&v2=${v2}`);
    return response.data;
  },

  // Delete a specific version
  deleteVersion: async (conversationId: string, slideId: string, version: number): Promise<{
    success: boolean;
    message: string;
  }> => {
    const response = await api.delete(`/conversations/${conversationId}/slides/${slideId}/versions/${version}`);
    return response.data;
  },

  // ====== SLIDE SHARING/PUBLISHING ======

  // Publish a slide (create a public share link)
  publish: async (conversationId: string, slideId: string, expiresInDays?: number): Promise<{
    success: boolean;
    shareLink: {
      id: string;
      shareToken: string;
      shareUrl: string;
      isActive: boolean;
      expiresAt: string | null;
      viewCount: number;
      lastViewedAt: string | null;
      createdAt: string;
      createdBy: string;
      revokedAt: string | null;
    };
  }> => {
    const response = await api.post(`/conversations/${conversationId}/slides/${slideId}/publish`, {
      expiresInDays
    });
    return response.data;
  },

  // Get publish status for a slide
  getPublishStatus: async (conversationId: string, slideId: string): Promise<{
    isPublished: boolean;
    shareLinks: Array<{
      id: string;
      shareToken: string;
      shareUrl: string;
      isActive: boolean;
      expiresAt: string | null;
      viewCount: number;
      lastViewedAt: string | null;
      createdAt: string;
      createdBy: string;
      revokedAt: string | null;
    }>;
  }> => {
    const response = await api.get(`/conversations/${conversationId}/slides/${slideId}/publish-status`);
    return response.data;
  },

  // Unpublish a slide (revoke a share link)
  unpublish: async (conversationId: string, slideId: string, shareLinkId: string): Promise<{
    success: boolean;
    message: string;
  }> => {
    const response = await api.delete(`/conversations/${conversationId}/slides/${slideId}/publish/${shareLinkId}`);
    return response.data;
  },

  // ====== START FROM IMAGE ======

  // Create a slide from a reference image (screenshot or design mockup)
  createFromImage: async (
    conversationId: string,
    imageFile: File,
    userInstructions?: string
  ): Promise<{
    success: boolean;
    slide: {
      id: string;
      slideIdentifier: string;
      title: string;
      sourceType: 'image';
      referenceImageUrl: string;
      referenceImagePath: string;
    };
    gcsPath: string;
    templatePath: string;
  }> => {
    const formData = new FormData();
    formData.append('image', imageFile);
    if (userInstructions) {
      formData.append('userInstructions', userInstructions);
    }

    const response = await api.post(
      `/conversations/${conversationId}/slides/from-image`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 120000, // 2 minutes for image upload
      }
    );
    return response.data;
  },
};

// Video Preview API
export const videoPreviewAPI = {
  // Find index.html files in working directory
  findIndexFiles: async (conversationId: string): Promise<{ indexFiles: string[]; count: number; baseDirectory: string }> => {
    const response = await api.get(`/filesystem/find-index?conversationId=${conversationId}`);
    return response.data;
  },

  // Get HTML serving URL for iframe
  // Includes impersonation user ID for admin "View As" functionality in iframes
  getHTMLUrl: (conversationId: string, relativePath: string): string => {
    const encodedPath = encodeURIComponent(relativePath);
    const baseUrl = getApiBaseUrl();
    const token = localStorage.getItem('bearer_token');
    const impersonateUserId = getImpersonationUserId();

    const params = new URLSearchParams();
    params.set('conversationId', conversationId);
    if (token) {
      params.set('token', token);
    }
    if (impersonateUserId) {
      params.set('impersonate', impersonateUserId);
    }
    return `${baseUrl}/filesystem/html/${encodedPath}?${params.toString()}`;
  },

  // Get JSON serving URL for GSAP slides
  // For GCS-backed slides, use slideId. For legacy, use path.
  // Includes impersonation user ID for admin "View As" functionality in iframes
  getJSONUrl: (conversationId: string, slideIdOrPath: string, version?: number): string => {
    const baseUrl = getApiBaseUrl();
    const token = localStorage.getItem('bearer_token');
    const impersonateUserId = getImpersonationUserId();

    // Detect if this is a slideId (format: "slide-001") or a path (contains slashes)
    const isSlideId = /^slide-\d+$/.test(slideIdOrPath) || !slideIdOrPath.includes('/');

    if (isSlideId) {
      // GCS-backed slide serving via SlideVersion table
      const params = new URLSearchParams();
      if (version !== undefined) {
        params.set('version', version.toString());
      }
      if (token) {
        params.set('token', token);
      }
      if (impersonateUserId) {
        params.set('impersonate', impersonateUserId);
      }
      const queryString = params.toString();
      return `${baseUrl}/conversations/${conversationId}/slides/${slideIdOrPath}/serve${queryString ? '?' + queryString : ''}`;
    } else {
      // Legacy filesystem path - fallback to old endpoint
      const encodedPath = encodeURIComponent(slideIdOrPath);
      const params = new URLSearchParams();
      params.set('conversationId', conversationId);
      if (token) {
        params.set('token', token);
      }
      if (impersonateUserId) {
        params.set('impersonate', impersonateUserId);
      }
      return `${baseUrl}/filesystem/json/${encodedPath}?${params.toString()}`;
    }
  },
};

// Project Initialization API
export const projectAPI = {
  // Initialize a new video project structure
  initialize: async (data: {
    conversationId: string;
    workingDirectory: string;
    projectStructure: any;
    initGit?: boolean;
  }): Promise<{ success: boolean; projectStructure: any }> => {
    const response = await api.post('/project/initialize', data);
    return response.data;
  },

  // Check if a conversation has an initialized project
  getStatus: async (conversationId: string): Promise<{
    initialized: boolean;
    projectId?: string;
    workingDirectory?: string;
  }> => {
    const response = await api.get(`/project/status/${conversationId}`);
    return response.data;
  },

  // Update project state
  updateState: async (conversationId: string, stateUpdate: any): Promise<{ success: boolean }> => {
    const response = await api.put(`/project/${conversationId}/state`, stateUpdate);
    return response.data;
  },

  // Get project state
  getState: async (conversationId: string): Promise<{ state: any }> => {
    const response = await api.get(`/project/${conversationId}/state`);
    return response.data;
  },
};

// Video Export API (per-slide MP4 export via BullMQ queue)
export interface VideoExportJob {
  id: string;
  slideIdentifier: string;
  version: number;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  progress: { percentage: number; currentOperation?: string };
  result?: { gcsPath?: string; fileSize?: number; duration?: number };
  error?: string;
  createdAt: string;
  completedAt?: string;
}

// Mirror of shared/services/videoExportOrchestrator.ts ExportDiff. Kept as a
// hand-typed interface to avoid pulling shared into the web-ui bundle.
export interface SlideVersionChange {
  outlineSlideId: string;
  slideIdentifier: string;
  title?: string;
  oldVersion: number | null;
  newVersion: number;
}

export interface ExportDiff {
  hasLastExport: boolean;
  lastExportAt: string | null;
  lastExportJobId: string | null;
  changes: {
    slides: SlideVersionChange[];
    backgroundMusic: {
      changed: boolean;
      old: { url: string; volume: number } | null;
      new: { url: string; volume: number } | null;
    };
    narratorVolume: {
      changed: boolean;
      old: number | null;
      new: number | null;
    };
  };
  summary: {
    slidesChanged: number;
    audioChanged: boolean;
    isClean: boolean;
  };
}

export type VideoExportOutputScale = '1080p' | '4k';

export const videoExportAPI = {
  // Start video export for a single slide
  start: async (
    conversationId: string,
    slideIdentifier: string,
    version: number,
    options?: { outputScale?: VideoExportOutputScale }
  ): Promise<{ success: boolean; jobId: string; message: string }> => {
    const response = await api.post('/video-export/start', {
      conversationId,
      slideIdentifier,
      version,
      // outputScale is advisory — server coerces against can4k entitlement.
      ...(options?.outputScale ? { outputScale: options.outputScale } : {}),
    });
    return response.data;
  },

  // Cancel a video export job
  cancel: async (jobId: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/video-export/cancel', { jobId });
    return response.data;
  },

  // Get video export job status
  getStatus: async (jobId: string): Promise<{
    success: boolean;
    id: string;
    status: string;
    progress: { percentage: number; currentOperation?: string };
    result?: Record<string, any>;
    error?: string;
    /** `type: 'program'` marks a program render: one job for the whole video. */
    metadata?: { type?: string };
  }> => {
    const response = await api.get(`/video-export/status/${jobId}`);
    return response.data;
  },

  // Start video export for ALL slides in a video conversation (batch)
  startAll: async (
    conversationId: string,
    options?: { force?: boolean; removeWatermark?: boolean; outputScale?: VideoExportOutputScale }
  ): Promise<{
    success: boolean;
    /** Parent ExportJob id — use with cancelExportJob / getExportJob. */
    exportJobId?: string;
    slides: Array<{
      outlineSlideId: string;
      childConversationId: string;
      slideIdentifier: string;
      version: number;
      jobId: string;
      skipped: boolean;
    }>;
    /** Present when the video is a program: one job renders it whole, so `slides` is empty. */
    program?: { status: 'enqueued' | 'in-flight' | 'cached'; jobId: string; scenes: number; frames: number };
  }> => {
    const response = await api.post('/video-export/start-all', {
      conversationId,
      ...(options?.force ? { force: true } : {}),
      // removeWatermark is advisory — server coerces against entitlement.
      ...(options?.removeWatermark ? { removeWatermark: true } : {}),
      // outputScale is advisory — server coerces against can4k entitlement.
      // 4K rejected with 402 + code FOUR_K_NOT_ALLOWED so the UI can prompt upgrade.
      ...(options?.outputScale ? { outputScale: options.outputScale } : {}),
    });
    return response.data;
  },

  // Cancel a running export (cascades to child jobs + signals workers)
  cancelExportJob: async (exportJobId: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/video-export/job/${exportJobId}/cancel`);
    return response.data;
  },

  // Get aggregate status for a parent ExportJob (used for polling)
  getExportJob: async (exportJobId: string): Promise<{
    success: boolean;
    id: string;
    conversationId: string;
    status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
    slidesTotal: number;
    slidesComplete: number;
    currentStage: string | null;
    outputUri: string | null;
    error: string | null;
    cancelRequested: boolean;
    percentage: number;
  }> => {
    const response = await api.get(`/video-export/job/${exportJobId}`);
    return response.data;
  },

  // List video export jobs for a conversation
  list: async (conversationId: string): Promise<{
    success: boolean;
    jobs: VideoExportJob[];
  }> => {
    const response = await api.get(`/video-export/list/${conversationId}`);
    return response.data;
  },

  // What changed since the last successful export?
  // Powers the publish-dropdown change badges + per-share-link "outdated"
  // indicators. Cheap to call on every dropdown open.
  getDiff: async (conversationId: string): Promise<ExportDiff> => {
    const response = await api.get(`/video-export/diff/${conversationId}`);
    return response.data;
  },

  // Assemble all exported slide MP4s into a single final video.
  // removeWatermark / outputScale MUST be forwarded: the server bakes both into
  // the assembly cache hash, so omitting them makes a no-watermark (or 4K)
  // request collide with — and return — a previously cached watermarked assembly.
  assemble: async (
    conversationId: string,
    options?: { force?: boolean; removeWatermark?: boolean; outputScale?: VideoExportOutputScale }
  ): Promise<{
    success: boolean;
    jobId: string;
    message: string;
  }> => {
    const response = await api.post('/video-export/assemble', {
      conversationId,
      ...(options?.force ? { force: true } : {}),
      // removeWatermark is advisory — server coerces against entitlement.
      ...(options?.removeWatermark ? { removeWatermark: true } : {}),
      // outputScale is advisory — server coerces against can4k entitlement.
      ...(options?.outputScale ? { outputScale: options.outputScale } : {}),
    });
    return response.data;
  },

  // Get signed download URL for an assembled video
  getDownloadUrl: async (jobId: string): Promise<{
    success: boolean;
    downloadUrl: string;
    fileSize?: number;
    duration?: number;
  }> => {
    const response = await api.get(`/video-export/download/${jobId}`);
    return response.data;
  },

  // Start PDF export for a video conversation (single atomic job)
  startPdf: async (conversationId: string): Promise<{
    success: boolean;
    jobId: string;
    message: string;
  }> => {
    const response = await api.post('/video-export/pdf', { conversationId });
    return response.data;
  },

  // Get PDF export state for a conversation
  getPdfState: async (conversationId: string): Promise<{
    success: boolean;
    status: string;
    jobId: string | null;
    progress: { percentage: number; currentOperation?: string } | null;
    error?: string | null;
    outputs?: { gcsPath?: string; fileSize?: number; pageCount?: number } | null;
    createdAt?: string;
    completedAt?: string | null;
  }> => {
    const response = await api.get(`/video-export/pdf-state/${conversationId}`);
    return response.data;
  },

  // Get full export state for a conversation (used to restore state on page load)
  getState: async (conversationId: string): Promise<{
    success: boolean;
    slideExports: Array<{
      outlineSlideId: string;
      childConversationId: string;
      slideIdentifier: string;
      version: number;
      jobId: string;
      status: string;
      progress: { percentage: number; currentOperation?: string } | null;
      error: string | null;
    }>;
    assembly: {
      jobId: string;
      status: string;
      progress: { percentage: number; currentOperation?: string } | null;
      outputs: { gcsPath?: string; fileSize?: number; duration?: number } | null;
      error: string | null;
      completedAt: string | null;
      isStale?: boolean;
      staleSlides?: string[];
    } | null;
    /**
     * Parent ExportJob id when there's an active export in flight. The hook
     * uses this to restore the cancel-button gate on a fresh tab reload — the
     * useRef holding the id cleared on remount, but the active-job lookup
     * here recovers it deterministically.
     */
    activeExportJobId?: string | null;
  }> => {
    const response = await api.get(`/video-export/state/${conversationId}`);
    return response.data;
  },
};

// Style API
export interface StyleColorPalette {
  primary: string;
  secondary: string;
  accent: string;
  accentSecondary?: string;
  accentTertiary?: string;
}

export interface StyleTypography {
  heading: string;
  body: string;
}

export interface Style {
  id: string;
  name: string;
  description: string;
  isDefault: boolean;
  isCustom: boolean;
  layouts: string[];
  colorPalette: StyleColorPalette;
  typography: StyleTypography;
  previewImage?: string;
  images?: string[];
  version: string;
  createdAt: string;
  updatedAt: string;
  filePath: string;
}

/** One of a person's saved looks, as the gallery shows it. */
export interface SavedLook {
  ref: string;
  scope: 'user' | 'team';
  modes: string[];
  style: string | null;
  theme: string | null;
  colors: string[];
}

export const looksAPI = {
  mine: async (): Promise<SavedLook[]> => {
    const response = await api.get('/looks/mine');
    return response.data.looks;
  },
};

export const styleAPI = {
  // Get all available styles
  getAll: async (): Promise<{ success: boolean; data: Style[] }> => {
    const response = await api.get('/styles');
    return response.data;
  },

  // Get a specific style by ID
  getById: async (styleId: string): Promise<{ success: boolean; data: Style }> => {
    const response = await api.get(`/styles/${styleId}`);
    return response.data;
  },

  // Get the default style
  getDefault: async (): Promise<{ success: boolean; data: Style }> => {
    const response = await api.get('/styles/default');
    return response.data;
  },

  // Export style from conversation to central repository
  export: async (exportData: {
    conversationId: string;
    name: string;
    description: string;
    styleId: string;
    colorPalette?: any;
    typography?: any;
  }): Promise<{ success: boolean; message: string; styleId: string; targetPath: string }> => {
    const response = await api.post('/styles/export', exportData);
    return response.data;
  },
};

// Style Session type definition
export interface StyleSession {
  sessionId: string;
  styleId: string;
  styleName: string;
  conversationId: string;
  createdAt: string;
  updatedAt: string;
}

// Style Session API
export const styleSessionAPI = {
  // List all style sessions
  list: async (): Promise<{ success: boolean; data: StyleSession[] }> => {
    const response = await api.get('/style-sessions');
    return response.data;
  },

  // Get style session by ID
  getById: async (sessionId: string): Promise<{ success: boolean; data: StyleSession }> => {
    const response = await api.get(`/style-sessions/${sessionId}`);
    return response.data;
  },

  // Create a new style session
  create: async (data: {
    styleId: string;
    conversationId: string
  }): Promise<{ success: boolean; data: StyleSession }> => {
    const response = await api.post('/style-sessions', data);
    return response.data;
  },

  // Delete a style session
  delete: async (sessionId: string): Promise<{ success: boolean }> => {
    const response = await api.delete(`/style-sessions/${sessionId}`);
    return response.data;
  },

  // Get git branches for a style session directory
  getBranches: async (sessionId: string): Promise<{
    branches: Array<{ name: string; isCurrent: boolean }>;
    currentBranch: string | null;
    sessionId: string;
    isGitRepo: boolean;
  }> => {
    const response = await api.get(`/style-sessions/${sessionId}/git/branches`);
    return response.data;
  },

  // Switch git branch for a style session directory
  switchBranch: async (sessionId: string, branch: string): Promise<{
    success: boolean;
    message: string;
    branch: string;
    previousBranch: string;
    sessionId: string;
    gitOutput: string;
  }> => {
    const response = await api.post(`/style-sessions/${sessionId}/git/switch`, { branch });
    return response.data;
  },
};

// Organization API
export const organizationAPI = {
  // Get user's organizations
  getAll: async (): Promise<{ success: boolean; organizations: any[] }> => {
    const response = await api.get('/organizations');
    return response.data;
  },

  // Get organization by ID
  getById: async (organizationId: string): Promise<{ success: boolean; organization: any }> => {
    const response = await api.get(`/organizations/${organizationId}`);
    return response.data;
  },

  // Get organization members
  getMembers: async (organizationId: string): Promise<{ success: boolean; members: any[] }> => {
    const response = await api.get(`/organizations/${organizationId}/members`);
    return response.data;
  },

  // Invite member to organization
  inviteMember: async (organizationId: string, data: { email: string; role: string }): Promise<{ success: boolean; invitation: any }> => {
    const response = await api.post(`/organizations/${organizationId}/members/invite`, data);
    return response.data;
  },

  // Update member role
  updateMemberRole: async (organizationId: string, memberId: string, data: { role: string }): Promise<{ success: boolean; result: any }> => {
    const response = await api.put(`/organizations/${organizationId}/members/${memberId}/role`, data);
    return response.data;
  },

  // Remove member from organization
  removeMember: async (organizationId: string, memberId: string): Promise<{ success: boolean; result: any }> => {
    const response = await api.delete(`/organizations/${organizationId}/members/${memberId}`);
    return response.data;
  },

  // Create organization
  create: async (data: { name: string; slug?: string }): Promise<{ success: boolean; organization: any }> => {
    const response = await api.post('/organizations', data);
    return response.data;
  }
};

// Team API
export const teamAPI = {
  // Get teams for organization
  getByOrganization: async (organizationId: string): Promise<{ success: boolean; teams: any[] }> => {
    const response = await api.get(`/organizations/${organizationId}/teams`);
    return response.data;
  },

  // Get team by ID
  getById: async (teamId: string): Promise<{ success: boolean; team: any }> => {
    const response = await api.get(`/teams/${teamId}`);
    return response.data;
  },

  // Create team
  create: async (organizationId: string, data: { name: string; description?: string }): Promise<{ success: boolean; team: any }> => {
    const response = await api.post(`/organizations/${organizationId}/teams`, data);
    return response.data;
  },

  // Update team
  update: async (teamId: string, data: { name?: string; description?: string }): Promise<{ success: boolean; team: any }> => {
    const response = await api.put(`/teams/${teamId}`, data);
    return response.data;
  },

  // Delete team
  delete: async (teamId: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/teams/${teamId}`);
    return response.data;
  },

  // Get team members
  getMembers: async (teamId: string): Promise<{ success: boolean; members: any[] }> => {
    const response = await api.get(`/teams/${teamId}/members`);
    return response.data;
  },

  // Add member to team
  addMember: async (teamId: string, data: { userId: string; role?: string }): Promise<{ success: boolean; team: any }> => {
    const response = await api.post(`/teams/${teamId}/members`, data);
    return response.data;
  },

  // Update team member role
  updateMemberRole: async (teamId: string, userId: string, data: { role: string }): Promise<{ success: boolean; team: any }> => {
    const response = await api.put(`/teams/${teamId}/members/${userId}/role`, data);
    return response.data;
  },

  // Remove member from team
  removeMember: async (teamId: string, userId: string): Promise<{ success: boolean; team: any }> => {
    const response = await api.delete(`/teams/${teamId}/members/${userId}`);
    return response.data;
  }
};

// Conversation style settings API
export const conversationStyleSettingsAPI = {
  // Get style settings for a conversation
  get: async (conversationId: string): Promise<{ success: boolean; data: any }> => {
    const response = await api.get(`/conversations/${conversationId}/style-settings`);
    return response.data;
  },

  // Update style settings for a conversation
  update: async (conversationId: string, settings: {
    selectedStyle: string;
    customStyleName?: string;
    customPrompt?: string;
    selectedPalette: string;
    customColors?: string;
  }): Promise<{ success: boolean; data: any }> => {
    const response = await api.put(`/conversations/${conversationId}/style-settings`, settings);
    return response.data;
  }
};

// Conversation avatar settings API
export const conversationAvatarSettingsAPI = {
  // Get avatar settings for a conversation
  get: async (conversationId: string): Promise<{ success: boolean; data: any }> => {
    const response = await api.get(`/conversations/${conversationId}/avatar-settings`);
    return response.data;
  },

  // Update avatar settings for a conversation
  update: async (conversationId: string, settings: {
    selectedAvatar: string;
    backgroundType: 'color' | 'image';
    selectedBackground: string;
  }): Promise<{ success: boolean; data: any }> => {
    const response = await api.put(`/conversations/${conversationId}/avatar-settings`, settings);
    return response.data;
  }
};

// Template Workflow API
export const templateAPI = {
  // Get validation results with slide data for Layouts tab
  getValidationResults: async (conversationId: string): Promise<{
    success: boolean;
    validation: {
      slides: Array<{
        slideId: string;
        slideNumber: number;
        pngPath: string;
        pngGcsUrl?: string;
        slideJsonPath?: string;
        slideJsonGcsUrl?: string;
        htmlPath: string;
        metadataPath?: string;
        hasGsapSlide: boolean;
        assets: string[];
      }>;
      totalSlides: number;
      validSlides: number;
      timestamp?: string;
    };
  }> => {
    const response = await api.get(`/conversations/${conversationId}/template/validation-results`);
    return response.data;
  },

  // Start layout generation for a specific slide
  startLayoutGeneration: async (conversationId: string, slideId: string): Promise<{
    success: boolean;
    jobId: string;
    slideId: string;
    message: string;
  }> => {
    const response = await api.post(`/conversations/${conversationId}/template/layouts/${slideId}/generate`);
    return response.data;
  },

  // Export all GSAP layouts as PNG screenshots in ZIP file
  exportLayouts: async (conversationId: string): Promise<Blob> => {
    const response = await api.post(
      `/conversations/${conversationId}/template/layouts/export`,
      {},
      {
        responseType: 'blob', // Important for binary data
        timeout: 300000, // 5 minutes for export operation
      }
    );
    return response.data;
  },

  // List public slide templates (hierarchical structure)
  listPublicTemplates: async (): Promise<{
    success: boolean;
    templates: Array<
      | {
        type: 'parent';
        id: string;
        name: string;
        description: string;
        colorPalette: {
          primary: string;
          secondary: string;
          accent: string;
          background?: string;
          text?: string;
          [key: string]: string | undefined;
        };
        typography?: {
          heading: string;
          body: string;
        };
        previewImage: string;
        layouts: Array<{
          id: string;
          name: string;
          slideJsonUrl: string;
          previewUrl?: string;
          parentId: string;
          metadata?: any;
        }>;
        isDefault?: boolean;
        isCustom?: boolean;
      }
      | {
        type: 'standalone';
        id: string;
        name: string;
        slideJsonUrl: string;
        previewUrl?: string;
        metadata?: any;
      }
    >;
  }> => {
    const response = await api.get('/templates/public');
    return response.data;
  },

  // Apply a template to a conversation
  applyTemplate: async (conversationId: string, templateId: string): Promise<{
    success: boolean;
    copiedToPath: string;
    destination: string;
    templateName: string;
    appliedTemplateId: string;
    appliedTemplateAt: string;
    message: string;
  }> => {
    const response = await api.post(
      `/conversations/${conversationId}/template/apply`,
      {
        templateName: templateId,
        destination: 'template', // Always use 'template' destination for public templates
      }
    );
    return response.data;
  }
};

// Signup API
export const signupAPI = {
  // Complete signup by creating organization and team
  complete: async (data: {
    organizationName: string;
    teamName: string;
    organizationDescription?: string;
    teamDescription?: string;
  }): Promise<{ success: boolean; data: any; waitListed?: boolean }> => {
    const response = await api.post('/signup/complete', data);
    return response.data;
  },

  // Check if user needs to complete signup
  getStatus: async (): Promise<{ success: boolean; data: { needsSetup: boolean; hasOrganizations: boolean; organizationCount: number; waitListed?: boolean } }> => {
    const response = await api.get('/signup/status');
    return response.data;
  },

  // Accept organization invitation
  acceptInvitation: async (data: { token: string }): Promise<{ success: boolean; data: any }> => {
    const response = await api.post('/signup/accept-invitation', data);
    return response.data;
  }
};

// Git API for session directories
export const gitAPI = {
  // Get git branches for a conversation's session directory
  getBranches: async (conversationId: string): Promise<{
    branches: Array<{ name: string; isCurrent: boolean }>;
    currentBranch: string;
    conversationId: string;
  }> => {
    const response = await api.get(`/sessions/${conversationId}/git/branches`);
    return response.data;
  },

  // Switch git branch for a conversation's session directory
  switchBranch: async (conversationId: string, branch: string): Promise<{
    success: boolean;
    previousBranch: string;
    currentBranch: string;
    conversationId: string;
    created: boolean;
  }> => {
    const response = await api.post(`/sessions/${conversationId}/git/switch`, { branch });
    return response.data;
  }
};

// Outline API - Pre-generation outline review step
import type {
  VideoOutline,
  SlideOutline,
  BrandingConfig,
  GetOutlineResponse,
  UpdateOutlineResponse,
  ApproveOutlineResponse,
  TemplateLayout,
} from '@/types/outline';

export const outlineAPI = {
  // Create outline with provided slides data
  create: async (
    conversationId: string,
    data: { templateId: string; templateName: string; slides: SlideOutline[] }
  ): Promise<{ success: boolean; outline: VideoOutline }> => {
    const response = await api.post(`/conversations/${conversationId}/outline/create`, data);
    return response.data;
  },

  // Get current outline for a conversation. Single-flight: at workspace mount
  // the shell and useOutlineSlideStatus both request it concurrently — the
  // server walks storage per slide to answer, so the duplicate is expensive.
  get: async (conversationId: string): Promise<GetOutlineResponse> =>
    coalesceRequest(`getOutline:${conversationId}`, async () => {
      const response = await api.get(`/conversations/${conversationId}/outline`);
      return response.data;
    }),

  // Save user modifications to the outline
  update: async (conversationId: string, outline: Partial<VideoOutline>): Promise<UpdateOutlineResponse> => {
    const response = await api.put(`/conversations/${conversationId}/outline`, outline);
    return response.data;
  },

  // Approve outline and trigger parallel slide generation
  approve: async (conversationId: string): Promise<ApproveOutlineResponse> => {
    const response = await api.post(`/conversations/${conversationId}/outline/approve`);
    return response.data;
  },

  // Reset outline to draft status
  reset: async (conversationId: string): Promise<{ success: boolean; status: string }> => {
    const response = await api.post(`/conversations/${conversationId}/outline/reset`);
    return response.data;
  },

  // Get available layouts from the applied template. Single-flight: requested
  // by both the shell and OutlineWorkspacePanel at mount.
  getTemplateLayouts: async (conversationId: string): Promise<{ layouts: TemplateLayout[] }> =>
    coalesceRequest(`getTemplateLayouts:${conversationId}`, async () => {
      const response = await api.get(`/conversations/${conversationId}/outline/layouts`);
      return response.data;
    }),

  // Generate a single slide from outline
  generateSlide: async (
    conversationId: string,
    slideId: string,
    options?: { provider?: 'gemini' | 'claude' }
  ): Promise<{
    success: boolean;
    slideConversationId: string;
    messageId: string;
    jobId: string;
  }> => {
    const response = await api.post(
      `/conversations/${conversationId}/outline/slides/${slideId}/generate`,
      options || {}
    );
    return response.data;
  },

  // Generate all slides from outline
  generateAllSlides: async (
    conversationId: string,
    options?: { provider?: 'gemini' | 'claude' }
  ): Promise<{
    success: boolean;
    slides: Array<{
      slideId: string;
      slideConversationId: string;
      messageId: string;
      jobId: string;
    }>;
  }> => {
    const response = await api.post(
      `/conversations/${conversationId}/outline/generate-all`,
      options || {}
    );
    return response.data;
  },

  // Get child slide conversations
  getChildConversations: async (
    conversationId: string
  ): Promise<{
    success: boolean;
    children: Array<{
      id: string;
      outlineSlideId: string;
      title: string;
      collectionType: string;
      createdAt: string;
    }>;
  }> => {
    const response = await api.get(`/conversations/${conversationId}/children`);
    return response.data;
  },

  // Generate narration scripts for all slides
  generateScripts: async (
    conversationId: string,
    options?: {
      userInstructions?: string;
      regenerate?: boolean;
    }
  ): Promise<{
    success: boolean;
    jobId: string;
    slideCount: number;
    hasExistingScripts: boolean;
  }> => {
    const response = await api.post(
      `/conversations/${conversationId}/outline/scripts/generate`,
      options || {}
    );
    return response.data;
  },

  // Get all scripts for a video's slides
  getScripts: async (
    conversationId: string
  ): Promise<{
    conversationId: string;
    scripts: Array<{
      slideId: string;          // Database UUID - used for API calls
      slideIdentifier: string;  // "slide-001" etc - used for display/sorting
      childConversationId: string;
      title: string;
      script: {
        scriptText: string | null;
        promptFlash: string | null;
        promptPro: string | null;
        preferredModel: string | null;
        audioUrl: string | null;
        audioDuration: number | null;
        updatedAt: string | null;
      } | null;
      hasAudio: boolean;
    }>;
    totalSlides: number;
    slidesWithScripts: number;
    slidesWithAudio: number;
    // Video-level script generation status
    hasVideoScripts: boolean;     // true if video-scripts.json exists
    hasNarrativeContext: boolean; // true if narrativeContext has video-level prompts
  }> =>
    // Coalesce concurrent fetches: on the studio view the background narration
    // preload (VideoWorkspacePanels) and VideoScriptsPanel both request scripts
    // at mount, hitting this ~1.3s endpoint twice. Single-flight collapses the
    // overlapping calls into one; a later refresh (post-edit) still re-fetches.
    coalesceRequest(`getScripts:${conversationId}`, async () => {
      const response = await api.get(`/conversations/${conversationId}/outline/scripts`);
      return response.data;
    }),

  // Edit scripts using natural language instructions (video-aware)
  editScripts: async (
    conversationId: string,
    options: {
      editInstructions: string;
      targetSlideIds?: string[];
    }
  ): Promise<{
    success: boolean;
    jobId: string;
    mode: string;
    editInstructions: string;
    targetSlideIds: string[];
  }> => {
    const response = await api.post(
      `/conversations/${conversationId}/outline/scripts/edit`,
      options
    );
    return response.data;
  },

  // Get narrative context (video-level TTS prompts and style)
  getNarrativeContext: async (
    conversationId: string
  ): Promise<{
    conversationId: string;
    narrativeContext: {
      openingHook?: string;
      keyTheme?: string;
      tone?: string;
      resolution?: string;
      baseVoiceStyle?: {
        pace?: string;
        personality?: string;
        tone?: string;
      };
      audioPrompt?: string;
      videoPromptFlash?: string;  // Legacy
      videoPromptPro?: string;    // Legacy
    } | null;
    lastUpdatedAt: string | null;
  }> => {
    const response = await api.get(`/conversations/${conversationId}/narration/context`);
    return response.data;
  },

  // Update narrative context TTS prompt
  updateNarrativeContext: async (
    conversationId: string,
    updates: {
      audioPrompt?: string;       // New single prompt
      videoPromptFlash?: string;  // Legacy
      videoPromptPro?: string;    // Legacy
    }
  ): Promise<{
    success: boolean;
    conversationId: string;
    narrativeContext: {
      openingHook?: string;
      keyTheme?: string;
      tone?: string;
      resolution?: string;
      baseVoiceStyle?: {
        pace?: string;
        personality?: string;
        tone?: string;
      };
      audioPrompt?: string;
      videoPromptFlash?: string;  // Legacy
      videoPromptPro?: string;    // Legacy
    };
  }> => {
    const response = await api.put(`/conversations/${conversationId}/narration/context`, updates);
    return response.data;
  },

  // Reset a generated slide for regeneration (delete child conversation and all versions)
  resetSlide: async (
    conversationId: string,
    slideId: string
  ): Promise<{
    success: boolean;
    slideId: string;
    message: string;
  }> => {
    const response = await api.delete(
      `/conversations/${conversationId}/outline/slides/${slideId}/reset`
    );
    return response.data;
  },

  // Cancel slide generation (non-destructive - just cancels the job, keeps data intact)
  // This is the SAFE stop button - use this instead of resetSlide for the Stop button
  // If slideConversationId is null, the orphan conversation was deleted (first-time cancel with no compiled version)
  cancelSlideGeneration: async (
    conversationId: string,
    slideId: string
  ): Promise<{
    success: boolean;
    slideId: string;
    previousStatus: 'pending' | 'completed';
    message: string;
    slideConversationId: string | null;
  }> => {
    const response = await api.post(
      `/conversations/${conversationId}/outline/slides/${slideId}/cancel`
    );
    return response.data;
  },

  // ====== SLIDE MANAGEMENT (Add, Delete, Restore, Reorder) ======

  // Add a new slide at a specific position
  addSlide: async (
    conversationId: string,
    data: {
      position: number;
      slideData?: Partial<SlideOutline>;
    }
  ): Promise<{
    success: boolean;
    slide: SlideOutline;
    outline: VideoOutline;
    changes: number;
  }> => {
    const response = await api.post(
      `/conversations/${conversationId}/outline/slides`,
      data
    );
    return response.data;
  },

  // Delete a slide (soft delete by default, hard delete with { hard: true })
  deleteSlide: async (
    conversationId: string,
    slideId: string,
    options?: { hard?: boolean }
  ): Promise<{
    success: boolean;
    slideId: string;
    deleteType: 'soft' | 'hard';
    outline: VideoOutline;
    changes: number;
  }> => {
    const response = await api.delete(
      `/conversations/${conversationId}/outline/slides/${slideId}${options?.hard ? '?hard=true' : ''}`
    );
    return response.data;
  },

  // Restore a soft-deleted slide
  restoreSlide: async (
    conversationId: string,
    slideId: string,
    options?: { position?: number }
  ): Promise<{
    success: boolean;
    slideId: string;
    originalId: string;
    position: number;
    outline: VideoOutline;
    changes: number;
  }> => {
    const response = await api.post(
      `/conversations/${conversationId}/outline/slides/${slideId}/restore`,
      options || {}
    );
    return response.data;
  },

  // Reorder slides (either by fromIndex/toIndex or full slideIds array)
  reorderSlides: async (
    conversationId: string,
    data: { fromIndex: number; toIndex: number } | { slideIds: string[] }
  ): Promise<{
    success: boolean;
    outline: VideoOutline;
    changes: number;
  }> => {
    const response = await api.put(
      `/conversations/${conversationId}/outline/slides/reorder`,
      data
    );
    return response.data;
  },

  // Get list of deleted slides
  getDeletedSlides: async (
    conversationId: string
  ): Promise<{
    deletedSlides: SlideOutline[];
    count: number;
  }> => {
    const response = await api.get(
      `/conversations/${conversationId}/outline/slides/deleted`
    );
    return response.data;
  },

  // Repair outline links - reconnect child conversations to planJson
  // Use when slideConversationId links are lost (e.g., after outline regeneration)
  repairLinks: async (
    conversationId: string
  ): Promise<{
    success: boolean;
    repaired: number;
    alreadyLinked: number;
    notFound: number;
    totalSlides: number;
    totalChildren: number;
    outline: VideoOutline;
  }> => {
    const response = await api.post(
      `/conversations/${conversationId}/outline/repair`
    );
    return response.data;
  },

  // ====== BRANDING (Company branding for video slides) ======

  // Update branding configuration
  updateBranding: async (
    conversationId: string,
    branding: BrandingConfig
  ): Promise<{
    success: boolean;
    branding: BrandingConfig;
  }> => {
    const response = await api.put(
      `/conversations/${conversationId}/outline/branding`,
      branding
    );
    return response.data;
  },

  // Upload logo for branding
  uploadLogo: async (
    conversationId: string,
    file: File
  ): Promise<{
    success: boolean;
    logo: {
      url: string;
      gcsPath: string;
      position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
      maxHeight: number;
    };
  }> => {
    const formData = new FormData();
    formData.append('logo', file);

    const response = await api.post(
      `/conversations/${conversationId}/outline/branding/logo`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  // Delete logo from branding
  deleteLogo: async (
    conversationId: string
  ): Promise<{
    success: boolean;
    message: string;
  }> => {
    const response = await api.delete(
      `/conversations/${conversationId}/outline/branding/logo`
    );
    return response.data;
  },

  // Clear all branding (reset to template defaults)
  clearBranding: async (
    conversationId: string
  ): Promise<{
    success: boolean;
    message: string;
  }> => {
    const response = await api.delete(
      `/conversations/${conversationId}/outline/branding`
    );
    return response.data;
  },
};

// Video Manifest API
// Import types from videoManifest.ts for type safety
import type { VideoManifest, TransitionConfig, ManifestSlide } from '@/types/videoManifest';

export const videoManifestAPI = {
  // Get video manifest for a conversation
  getManifest: async (conversationId: string): Promise<VideoManifest | null> => {
    const response = await api.get(`/conversations/${conversationId}/video/manifest`);
    return response.data;
  },

  // Save/update video manifest
  saveManifest: async (
    conversationId: string,
    manifest: VideoManifest
  ): Promise<{
    success: boolean;
    slideCount: number;
    totalDuration: number;
  }> => {
    const response = await api.put(`/conversations/${conversationId}/video/manifest`, manifest);
    return response.data;
  },

  // Build manifest from outline slides (and save it)
  buildManifest: async (
    conversationId: string,
    options?: {
      defaultTransition?: TransitionConfig;
      apiBaseUrl?: string;
    }
  ): Promise<{
    success: boolean;
    manifest: VideoManifest;
  }> => {
    const response = await api.post(
      `/conversations/${conversationId}/video/manifest/build`,
      options || {}
    );
    return response.data;
  },

  // Refresh manifest by rebuilding from current state
  // Useful after slide generation completes or audio is added
  refreshManifest: async (
    conversationId: string,
    options?: {
      defaultTransition?: TransitionConfig;
      apiBaseUrl?: string;
    }
  ): Promise<{
    success: boolean;
    manifest: VideoManifest;
  }> => {
    const response = await api.post(
      `/conversations/${conversationId}/video/manifest/refresh`,
      options || {}
    );
    return response.data;
  },

  // Update a single slide in the manifest
  updateSlide: async (
    conversationId: string,
    slideId: string,
    updates: Partial<ManifestSlide>
  ): Promise<{
    success: boolean;
    manifest: VideoManifest;
  }> => {
    const response = await api.patch(
      `/conversations/${conversationId}/video/manifest/slides/${slideId}`,
      updates
    );
    return response.data;
  },

  // Reorder slides in the manifest
  reorderSlides: async (
    conversationId: string,
    slideIds: string[]
  ): Promise<{
    success: boolean;
    manifest: VideoManifest;
  }> => {
    const response = await api.post(
      `/conversations/${conversationId}/video/manifest/reorder`,
      { slideIds }
    );
    return response.data;
  },

  // Delete video manifest
  deleteManifest: async (conversationId: string): Promise<{ success: boolean }> => {
    const response = await api.delete(`/conversations/${conversationId}/video/manifest`);
    return response.data;
  },

  // Update transition for a specific slide
  // Convenience wrapper for updateSlide focused on transitions
  updateSlideTransition: async (
    conversationId: string,
    slideId: string,
    transition: TransitionConfig
  ): Promise<{
    success: boolean;
    manifest: VideoManifest;
  }> => {
    const response = await api.patch(
      `/conversations/${conversationId}/video/manifest/slides/${slideId}`,
      { transitionOut: transition }
    );
    return response.data;
  },

  // Clear slide's custom transition (revert to default)
  clearSlideTransition: async (
    conversationId: string,
    slideId: string
  ): Promise<{
    success: boolean;
    manifest: VideoManifest;
  }> => {
    const response = await api.patch(
      `/conversations/${conversationId}/video/manifest/slides/${slideId}`,
      { transitionOut: null }
    );
    return response.data;
  },

  // Update the default transition for the entire video
  updateDefaultTransition: async (
    conversationId: string,
    transition: TransitionConfig
  ): Promise<{
    success: boolean;
    manifest: VideoManifest;
  }> => {
    // First get current manifest
    const currentManifest = await videoManifestAPI.getManifest(conversationId);
    if (!currentManifest) {
      throw new Error('Manifest not found');
    }

    // Update default transition
    const updatedManifest: VideoManifest = {
      ...currentManifest,
      defaultTransition: transition,
    };

    // Save updated manifest
    await api.put(`/conversations/${conversationId}/video/manifest`, updatedManifest);

    return {
      success: true,
      manifest: updatedManifest,
    };
  },

  // Update background music for the entire video
  updateBackgroundMusic: async (
    conversationId: string,
    backgroundMusic: {
      url: string;
      volume: number;
      loop: boolean;
    } | null
  ): Promise<{
    success: boolean;
    manifest: VideoManifest;
  }> => {
    // First get current manifest
    const currentManifest = await videoManifestAPI.getManifest(conversationId);
    if (!currentManifest) {
      throw new Error('Manifest not found');
    }

    // Update background music (or remove if null)
    const updatedManifest: VideoManifest = {
      ...currentManifest,
    };

    if (backgroundMusic) {
      updatedManifest.backgroundMusic = backgroundMusic;
    } else {
      delete updatedManifest.backgroundMusic;
    }

    // Save updated manifest
    await api.put(`/conversations/${conversationId}/video/manifest`, updatedManifest);

    return {
      success: true,
      manifest: updatedManifest,
    };
  },

  // Update audio mix settings (narrator volume, etc.)
  updateAudioMix: async (
    conversationId: string,
    audioMix: {
      narratorVolume: number;
    }
  ): Promise<{
    success: boolean;
    manifest: VideoManifest;
  }> => {
    // First get current manifest
    const currentManifest = await videoManifestAPI.getManifest(conversationId);
    if (!currentManifest) {
      throw new Error('Manifest not found');
    }

    // Update audio mix settings
    const updatedManifest: VideoManifest = {
      ...currentManifest,
      audioMix: {
        ...(currentManifest.audioMix || {}),
        ...audioMix,
      },
    };

    // Save updated manifest
    await api.put(`/conversations/${conversationId}/video/manifest`, updatedManifest);

    return {
      success: true,
      manifest: updatedManifest,
    };
  },
};

// Background Music API (video-level background music)
export const backgroundMusicAPI = {
  // Upload a music file to GCS and return the public URL
  upload: async (
    conversationId: string,
    file: File
  ): Promise<{
    success: boolean;
    url: string;
    gcsPath: string;
    filename: string;
    size: number;
    mimeType: string;
  }> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post(
      `/conversations/${conversationId}/background-music/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 60000, // 60 seconds for large audio files
      }
    );
    return response.data;
  },

  // Validate a URL is a valid audio file (by checking headers)
  validateUrl: async (url: string): Promise<{
    valid: boolean;
    contentType?: string;
    error?: string;
  }> => {
    try {
      // Fetch headers only to check content type
      const response = await fetch(url, { method: 'HEAD' });
      if (!response.ok) {
        return { valid: false, error: `HTTP ${response.status}` };
      }

      const contentType = response.headers.get('content-type') || '';
      const isAudio = contentType.startsWith('audio/');

      return {
        valid: isAudio,
        contentType,
        error: isAudio ? undefined : `Not an audio file: ${contentType}`,
      };
    } catch (error) {
      return {
        valid: false,
        error: error instanceof Error ? error.message : 'Failed to validate URL',
      };
    }
  },
};

// Video Share API (public sharing of video compositions)
export interface VideoShareLinkInfo {
  id: string;
  shareToken: string;
  /** Web-ui url. Reviewer links must be built from this — `?review=true` only works here. */
  shareUrl: string;
  /**
   * Static player url (play.4klabs.ai/{token}/) — the only one that unfurls a link
   * preview. Null until the bake lands, so a copied link is never a 404.
   */
  playUrl?: string | null;
  embedUrl: string;
  /** Baked static CDN player bundle URL (cdn.4klabs.ai/vplay/…), or null if not baked. */
  staticBundleUrl?: string | null;
  isActive: boolean;
  expiresAt: Date | null;
  viewCount: number;
  lastViewedAt: Date | null;
  createdAt: Date;
  updatedAt: string | null;
  createdBy: string;
  revokedAt: Date | null;
  thumbnailUrl: string | null;
  title: string | null;
  slideCount: number;
  totalDuration: number;
  /** True when one or more pinned slide versions diverge from current. */
  isOutdated?: boolean;
  /** How many slides are out of date in this link. */
  outdatedSlideCount?: number;
  /** Whether reviewer comments via ?review=true are accepted on this link. */
  allowReviews?: boolean;
}

export const videoShareAPI = {
  // Publish a video (create share link with version-pinned manifest)
  publish: async (conversationId: string): Promise<{
    success: boolean;
    shareLink: VideoShareLinkInfo;
  }> => {
    const response = await api.post(`/conversations/${conversationId}/video/publish`);
    return response.data;
  },

  // Get publish status for a video
  getPublishStatus: async (conversationId: string): Promise<{
    isPublished: boolean;
    shareLinks: VideoShareLinkInfo[];
  }> => {
    const response = await api.get(`/conversations/${conversationId}/video/publish-status`);
    return response.data;
  },

  // Update a share link's frozen manifest to latest slide versions (same URL)
  update: async (conversationId: string, shareLinkId: string): Promise<{
    success: boolean;
    shareLink: VideoShareLinkInfo;
  }> => {
    const response = await api.put(`/conversations/${conversationId}/video/publish/${shareLinkId}`);
    return response.data;
  },

  // Unpublish (revoke) a video share link
  unpublish: async (conversationId: string, shareLinkId: string): Promise<{
    success: boolean;
    message: string;
  }> => {
    const response = await api.delete(`/conversations/${conversationId}/video/publish/${shareLinkId}`);
    return response.data;
  },

  // Toggle whether reviewers can comment via ?review=true on this share link
  setAllowReviews: async (conversationId: string, shareLinkId: string, allowReviews: boolean): Promise<{
    success: boolean;
    shareLink: VideoShareLinkInfo;
  }> => {
    const response = await api.patch(
      `/conversations/${conversationId}/video/publish/${shareLinkId}/settings`,
      { allowReviews },
    );
    return response.data;
  },
};

// ============================================================================
// Project Sharing API (User-to-User)
// ============================================================================

export interface ProjectShareInfo {
  id: string;
  conversationId: string;
  projectTitle: string;
  permission: 'VIEW' | 'CLONE';
  isActive: boolean;
  expiresAt: string | null;
  createdAt: string;
  revokedAt: string | null;
  sharedBy: { id: string; email: string; displayName: string | null };
  sharedWith: { id: string; email: string; displayName: string | null };
  thumbnailUrl?: string | null;
  slideCount?: number;
}

export interface SharedProjectPreview {
  id: string;
  title: string;
  slideCount: number;
  thumbnailUrl: string | null;
  createdAt: string;
  owner: { displayName: string | null; email: string };
  permission: 'VIEW' | 'CLONE';
}

export const projectShareAPI = {
  // Share a project with a user by email
  share: async (conversationId: string, email: string, permission: 'VIEW' | 'CLONE' = 'CLONE'): Promise<{
    success: boolean;
    share: ProjectShareInfo;
  }> => {
    const response = await api.post(`/conversations/${conversationId}/shares`, { email, permission });
    return response.data;
  },

  // List shares on a project (owner only)
  listShares: async (conversationId: string): Promise<{
    success: boolean;
    shares: ProjectShareInfo[];
  }> => {
    const response = await api.get(`/conversations/${conversationId}/shares`);
    return response.data;
  },

  // Revoke a share
  revoke: async (conversationId: string, shareId: string): Promise<{
    success: boolean;
    message: string;
  }> => {
    const response = await api.delete(`/conversations/${conversationId}/shares/${shareId}`);
    return response.data;
  },

  // List projects shared with the current user
  sharedWithMe: async (): Promise<{
    success: boolean;
    shares: ProjectShareInfo[];
  }> => {
    const response = await api.get('/shared-with-me');
    return response.data;
  },

  // Preview a shared project
  preview: async (shareId: string): Promise<{
    success: boolean;
    preview: SharedProjectPreview;
  }> => {
    const response = await api.get(`/shared-with-me/${shareId}/preview`);
    return response.data;
  },

  // Clone a shared project into the current user's account
  clone: async (shareId: string, newTitle?: string): Promise<{
    success: boolean;
    newProjectId: string;
    dashboardUrl: string;
    slidesCopied: number;
    audioCopied: number;
    filesCopied: number;
  }> => {
    const response = await api.post(`/shared-with-me/${shareId}/clone`, { newTitle });
    return response.data;
  },
};

// ============================================================================
// Script-First / Audio-First API
// ============================================================================

export const scriptAudioAPI = {
  /**
   * Process a user-provided narration script
   * Stores it as a Resource and returns word count / estimated duration
   */
  processScript: async (conversationId: string, scriptText: string): Promise<{
    success: boolean;
    wordCount: number;
    estimatedDuration: number;
    resourceId: string;
  }> => {
    const response = await api.post(`/conversations/${conversationId}/process-script`, { scriptText });
    return response.data;
  },

  /**
   * Queue audio transcription on the worker.
   * Results arrive via SSE transcription events (transcription_complete / transcription_error).
   */
  transcribeAudio: async (conversationId: string, resourceId: string): Promise<{
    success: boolean;
    jobId: string;
    message: string;
  }> => {
    const response = await api.post(
      `/conversations/${conversationId}/transcribe-audio`,
      { resourceId },
    );
    return response.data;
  },

  // NOTE: sliceAudio removed — audio-first videos now use continuous master narration
};

export default api;